// hooks/usePlansActionCached.js - Version avec cache intelligent
import { useCallback, useEffect, useState } from 'react';
import { useCacheManager } from './useCacheManager';

const API_BASE_URL = process.env.REACT_APP_USER_BACKEND_API_URL || 'http://localhost:5003/api';

export const usePlansActionCached = (options = {}) => {
    const {
        objectif_id = null,
        status = null,
        priority = null,
        includeDetails = false,
        cacheDuration = 5 * 60 * 1000, // 5 minutes
        forceRefresh = false,
        autoRefresh = true,
        refreshInterval = 30000
    } = options;

    // États
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [isFromCache, setIsFromCache] = useState(false);

    // Hook de cache
    const { getCacheKey, setCacheData, getCacheData, invalidateCache } = useCacheManager();

    // Headers pour les requêtes
    const getHeaders = useCallback(() => ({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`
    }), []);

    // Fonction pour gérer les erreurs API
    const handleApiError = useCallback((error, context = '') => {
        let message;
        if (error.response?.status === 401) {
            message = 'Session expirée, veuillez vous reconnecter';
            localStorage.removeItem('access_token');
        } else if (error.response?.status === 403) {
            message = 'Accès non autorisé';
        } else if (error.response?.status >= 500) {
            message = 'Erreur serveur, veuillez réessayer plus tard';
        } else {
            message = error.response?.data?.error || error.message || 'Une erreur est survenue';
        }
        
        setError(error);
        console.error(`❌ Erreur plans d'action (${context}):`, message);
        return { success: false, error: message };
    }, []);

    // 📋 CHARGER TOUS LES PLANS D'ACTION AVEC CACHE
    const fetchPlans = useCallback(async (customOptions = {}, useCache = true) => {
        // Préparer la clé de cache
        const options = {
            objectif_id,
            status,
            priority,
            includeDetails,
            ...customOptions
        };
        const cacheKey = getCacheKey('plans_action', options);

        // Vérifier le cache d'abord (sauf si forceRefresh)
        if (useCache && !forceRefresh) {
            const cachedData = getCacheData(cacheKey);
            if (cachedData) {
                console.log('📦 Plans d\'action chargés depuis le cache');
                setPlans(cachedData);
                setIsFromCache(true);
                setError(null);
                return { success: true, plans: cachedData, fromCache: true };
            }
        }

        const token = localStorage.getItem('access_token');
        if (!token) {
            return handleApiError({ message: 'Token manquant' }, 'fetchPlans');
        }

        setLoading(true);
        setError(null);
        setIsFromCache(false);

        try {
            const params = new URLSearchParams();
            if (options.objectif_id) params.append('objectif_id', options.objectif_id);
            if (options.status) params.append('status', options.status);
            if (options.priority) params.append('priority', options.priority);
            if (options.includeDetails) params.append('details', 'true');

            const response = await fetch(
                `${API_BASE_URL}/plans-action?${params.toString()}`,
                {
                    method: 'GET',
                    headers: getHeaders()
                }
            );

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
            }

            const data = await response.json();
            const validPlans = (data.plans_action || []).filter(plan => plan.id_plan);

            // Sauvegarder dans le cache
            setCacheData(cacheKey, validPlans, cacheDuration);
            
            setPlans(validPlans);
            setLastUpdated(new Date());
            
            console.log(`✅ ${validPlans.length} plans d'action chargés depuis l'API`);
            return { success: true, plans: validPlans, fromCache: false };
        } catch (err) {
            return handleApiError(err, 'fetchPlans');
        } finally {
            setLoading(false);
        }
    }, [objectif_id, status, priority, includeDetails, getCacheKey, getCacheData, setCacheData, getHeaders, handleApiError, forceRefresh, cacheDuration]);

    // 🆕 CRÉER UN PLAN D'ACTION avec mise à jour optimiste
    const createPlan = useCallback(async (planData) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return handleApiError({ message: 'Token manquant' }, 'createPlan');
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE
        const tempPlan = {
            id_plan: `temp-${Date.now()}`,
            title: planData.title,
            description: planData.description || '',
            id_objectif: planData.id_objectif,
            priority: planData.priority || 'medium',
            status: 'a_faire',
            tasks: planData.tasks || [],
            total_tasks: (planData.tasks || []).length,
            completed_tasks: 0,
            progress_percentage: 0,
            isOptimistic: true
        };

        // Ajouter immédiatement à l'état local
        setPlans(prev => [tempPlan, ...prev]);

        try {
            // Validation côté client
            if (!planData.title || !planData.id_objectif) {
                throw new Error('Titre et ID objectif sont requis');
            }

            if (!planData.tasks || !Array.isArray(planData.tasks) || planData.tasks.length === 0) {
                throw new Error('Au moins une tâche est requise');
            }

            // Nettoyage des données
            const cleanData = {
                title: planData.title,
                description: planData.description || '',
                id_objectif: planData.id_objectif,
                priority: planData.priority || 'medium',
                start_date: planData.start_date || null,
                target_date: planData.target_date || null,
                ordre_affichage: parseInt(planData.ordre_affichage) || 0,
                tasks: planData.tasks.map((task, index) => ({
                    title: task.title?.trim() || `Tâche ${index + 1}`,
                    description: task.description?.trim() || '',
                    priority: task.priority || 'medium',
                    status: task.status || 'a_faire',
                    deadline: task.deadline || null,
                    estimated_hours: task.estimated_hours && !isNaN(parseFloat(task.estimated_hours)) ? parseFloat(task.estimated_hours) : null,
                    subtasks: (task.subtasks || []).map((subtask, subIndex) => ({
                        title: subtask.title?.trim() || `Sous-tâche ${subIndex + 1}`,
                        description: subtask.description?.trim() || '',
                        priority: subtask.priority || 'medium',
                        status: subtask.status || 'a_faire',
                        deadline: subtask.deadline || null,
                        estimated_hours: subtask.estimated_hours && !isNaN(parseFloat(subtask.estimated_hours)) ? parseFloat(subtask.estimated_hours) : null
                    }))
                }))
            };

            const response = await fetch(`${API_BASE_URL}/plans-action`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify(cleanData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();

            if (!data.plan_action || !data.plan_action.id_plan) {
                throw new Error('Réponse invalide du serveur: id_plan manquant');
            }

            // Invalider le cache
            invalidateCache('plans_action');
            console.log('🗑️ Cache plans d\'action invalidé après création');

            // Remplacer le plan optimiste par le vrai
            setPlans(prev => prev.map(plan => 
                plan.id_plan === tempPlan.id_plan ? data.plan_action : plan
            ));

            return { success: true, plan: data.plan_action };
        } catch (err) {
            // Retirer le plan optimiste en cas d'erreur
            setPlans(prev => prev.filter(plan => plan.id_plan !== tempPlan.id_plan));
            return handleApiError(err, 'createPlan');
        } finally {
            setLoading(false);
        }
    }, [getHeaders, handleApiError, invalidateCache]);

    // ✏️ MODIFIER UN PLAN D'ACTION avec mise à jour optimiste
    const updatePlan = useCallback(async (planId, updateData) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return handleApiError({ message: 'Token manquant' }, 'updatePlan');
        }

        if (!planId) {
            return handleApiError({ message: 'ID plan requis pour la mise à jour' }, 'updatePlan');
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousPlans = [...plans];
        setPlans(prev =>
            prev.map(plan =>
                plan.id_plan === planId
                    ? { ...plan, ...updateData, isOptimistic: true }
                    : plan
            )
        );

        try {
            // Nettoyage des données
            const cleanData = {};
            const allowedFields = [
                'title', 'description', 'priority', 'status',
                'start_date', 'target_date', 'ordre_affichage', 'tasks'
            ];

            for (const [key, value] of Object.entries(updateData)) {
                if (allowedFields.includes(key) && value !== undefined) {
                    if (key === 'ordre_affichage') {
                        cleanData[key] = parseInt(value);
                    } else if (key === 'tasks' && Array.isArray(value)) {
                        cleanData[key] = value.map((task, index) => ({
                            title: task.title?.trim() || `Tâche ${index + 1}`,
                            description: task.description?.trim() || '',
                            priority: task.priority || 'medium',
                            status: task.status || 'a_faire',
                            deadline: task.deadline || null,
                            estimated_hours: task.estimated_hours && !isNaN(parseFloat(task.estimated_hours)) ? parseFloat(task.estimated_hours) : null,
                            subtasks: (task.subtasks || []).map((subtask, subIndex) => ({
                                title: subtask.title?.trim() || `Sous-tâche ${subIndex + 1}`,
                                description: subtask.description?.trim() || '',
                                priority: subtask.priority || 'medium',
                                status: subtask.status || 'a_faire',
                                deadline: subtask.deadline || null,
                                estimated_hours: subtask.estimated_hours && !isNaN(parseFloat(subtask.estimated_hours)) ? parseFloat(subtask.estimated_hours) : null
                            }))
                        }));
                    } else {
                        cleanData[key] = value;
                    }
                }
            }

            const response = await fetch(`${API_BASE_URL}/plans-action/${planId}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(cleanData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();

            if (!data.plan_action || !data.plan_action.id_plan) {
                throw new Error('Réponse invalide du serveur: plan mis à jour invalide');
            }

            // Invalider le cache
            invalidateCache('plans_action');
            console.log('🗑️ Cache plans d\'action invalidé après modification');

            // Mettre à jour avec les vraies données
            setPlans(prev =>
                prev.map(plan =>
                    plan.id_plan === planId ? data.plan_action : plan
                )
            );

            return { success: true, plan: data.plan_action };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setPlans(previousPlans);
            return handleApiError(err, 'updatePlan');
        } finally {
            setLoading(false);
        }
    }, [plans, getHeaders, handleApiError, invalidateCache]);

    // 🗑️ SUPPRIMER UN PLAN D'ACTION avec mise à jour optimiste
    const deletePlan = useCallback(async (planId) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return handleApiError({ message: 'Token manquant' }, 'deletePlan');
        }

        if (!planId) {
            return handleApiError({ message: 'ID plan requis pour la suppression' }, 'deletePlan');
        }

        setLoading(true);
        setError(null);

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousPlans = [...plans];
        setPlans(prev => prev.filter(plan => plan.id_plan !== planId));

        try {
            const response = await fetch(`${API_BASE_URL}/plans-action/${planId}`, {
                method: 'DELETE',
                headers: getHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            // Invalider le cache
            invalidateCache('plans_action');
            console.log('🗑️ Cache plans d\'action invalidé après suppression');

            const data = await response.json();
            return { success: true, message: data.message };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setPlans(previousPlans);
            return handleApiError(err, 'deletePlan');
        } finally {
            setLoading(false);
        }
    }, [plans, getHeaders, handleApiError, invalidateCache]);

    // ⭐ METTRE À JOUR LE STATUT D'UNE TÂCHE avec mise à jour optimiste
    const updateTaskStatus = useCallback(async (planId, taskIndex, status, subtaskIndex = null) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return handleApiError({ message: 'Token manquant' }, 'updateTaskStatus');
        }

        if (!planId || taskIndex === undefined || !status) {
            return handleApiError({ message: 'Paramètres requis manquants pour la mise à jour de tâche' }, 'updateTaskStatus');
        }

        // Validation du statut
        const validStatuses = ['a_faire', 'en_cours', 'fait', 'annule'];
        if (!validStatuses.includes(status)) {
            return handleApiError({ message: 'Statut invalide' }, 'updateTaskStatus');
        }

        // MISE À JOUR OPTIMISTE - sauvegarder pour rollback
        const previousPlans = [...plans];
        setPlans(prev =>
            prev.map(plan => {
                if (plan.id_plan === planId) {
                    const updatedPlan = { ...plan, isOptimistic: true };
                    let tasks = [];
                    
                    if (typeof plan.tasks === 'string') {
                        try {
                            tasks = JSON.parse(plan.tasks);
                        } catch (e) {
                            tasks = [];
                        }
                    } else if (Array.isArray(plan.tasks)) {
                        tasks = [...plan.tasks];
                    }

                    if (tasks[taskIndex]) {
                        if (subtaskIndex !== null && tasks[taskIndex].subtasks && tasks[taskIndex].subtasks[subtaskIndex]) {
                            tasks[taskIndex].subtasks[subtaskIndex].status = status;
                        } else {
                            tasks[taskIndex].status = status;
                        }
                    }

                    updatedPlan.tasks = tasks;
                    return updatedPlan;
                }
                return plan;
            })
        );

        try {
            const requestBody = { status };
            if (subtaskIndex !== null) {
                requestBody.subtask_index = subtaskIndex;
            }

            const response = await fetch(`${API_BASE_URL}/plans-action/${planId}/tasks/${taskIndex}`, {
                method: 'PUT',
                headers: getHeaders(),
                body: JSON.stringify(requestBody)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();

            // Invalider le cache
            invalidateCache('plans_action');
            console.log('🗑️ Cache plans d\'action invalidé après mise à jour tâche');

            // Mettre à jour les statistiques du plan
            setPlans(prev =>
                prev.map(plan =>
                    plan.id_plan === planId
                        ? {
                            ...plan,
                            total_tasks: data.total_tasks,
                            completed_tasks: data.completed_tasks,
                            progress_percentage: data.progress_percentage,
                            isOptimistic: false
                        }
                        : plan
                )
            );

            return { success: true, data };
        } catch (err) {
            // ROLLBACK en cas d'erreur
            setPlans(previousPlans);
            return handleApiError(err, 'updateTaskStatus');
        }
    }, [plans, getHeaders, handleApiError, invalidateCache]);

    // 🔍 RÉCUPÉRER UN PLAN SPÉCIFIQUE AVEC DÉTAILS
    const getPlanById = useCallback(async (planId) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return handleApiError({ message: 'Token manquant' }, 'getPlanById');
        }

        if (!planId) {
            return handleApiError({ message: 'ID plan requis' }, 'getPlanById');
        }

        try {
            const response = await fetch(`${API_BASE_URL}/plans-action/${planId}`, {
                method: 'GET',
                headers: getHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            return { success: true, data };
        } catch (err) {
            return handleApiError(err, 'getPlanById');
        }
    }, [getHeaders, handleApiError]);

    // 🔍 RECHERCHER DANS LES PLANS ET TÂCHES
    const searchPlans = useCallback(async (query) => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            return handleApiError({ message: 'Token manquant' }, 'searchPlans');
        }

        if (!query || !query.trim() || query.trim().length < 2) {
            return handleApiError({ message: 'Le terme de recherche doit contenir au moins 2 caractères' }, 'searchPlans');
        }

        try {
            const response = await fetch(`${API_BASE_URL}/plans-action/search?q=${encodeURIComponent(query.trim())}`, {
                method: 'GET',
                headers: getHeaders()
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || `HTTP ${response.status}`);
            }

            const data = await response.json();
            return { success: true, data };
        } catch (err) {
            return handleApiError(err, 'searchPlans');
        }
    }, [getHeaders, handleApiError]);

    // 🔄 RAFRAÎCHIR LES PLANS (force le refetch)
    const refreshPlans = useCallback(() => {
        console.log('🔄 Force refresh des plans d\'action');
        return fetchPlans({}, false); // false = ne pas utiliser le cache
    }, [fetchPlans]);

    // 🎯 REFETCH avec gestion intelligente du cache
    const refetchPlans = useCallback(async () => {
        console.log('🔄 Refetch intelligent des plans d\'action');
        
        // Si les données sont récentes (moins de 1 minute), ne pas refetch
        if (lastUpdated && (Date.now() - lastUpdated.getTime()) < 60000 && !forceRefresh) {
            console.log('⏭️ Données récentes, skip refetch');
            return { success: true, plans, fromCache: true };
        }
        
        return fetchPlans({}, false);
    }, [fetchPlans, lastUpdated, forceRefresh, plans]);

    // 🔄 CHARGER LES PLANS AU MONTAGE
    useEffect(() => {
        if (autoRefresh) {
            fetchPlans();
        }
    }, [fetchPlans, autoRefresh]);

    // 🔄 AUTO-REFRESH OPTIONNEL
    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!autoRefresh || !token || refreshInterval <= 0) return;

        const interval = setInterval(() => {
            fetchPlans({}, false); // Force refresh périodique
        }, refreshInterval);
        
        return () => clearInterval(interval);
    }, [autoRefresh, refreshInterval, fetchPlans]);

    // 🧹 NETTOYER LES ERREURS
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    // 📊 CALCULS DÉRIVÉS POUR LES STATISTIQUES LOCALES
    const stats = {
        total: plans.length,
        aFaire: plans.filter(plan => plan.status === 'a_faire').length,
        enCours: plans.filter(plan => plan.status === 'en_cours').length,
        faits: plans.filter(plan => plan.status === 'fait').length,
        annules: plans.filter(plan => plan.status === 'annule').length,
        prioriteHaute: plans.filter(plan => plan.priority === 'high').length,
        prioriteMoyenne: plans.filter(plan => plan.priority === 'medium').length,
        prioriteBasse: plans.filter(plan => plan.priority === 'low').length,
        progressionMoyenne: plans.length > 0
            ? plans.reduce((sum, plan) => sum + (plan.progress_percentage || 0), 0) / plans.length
            : 0,
        totalTaches: plans.reduce((sum, plan) => sum + (plan.total_tasks || 0), 0),
        tachesCompletes: plans.reduce((sum, plan) => sum + (plan.completed_tasks || 0), 0)
    };

    return {
        // Données
        plans,
        stats,
        lastUpdated,

        // États
        loading,
        error,
        isFromCache,

        // Actions CRUD avec mise à jour optimiste
        createPlan,
        updatePlan,
        deletePlan,
        updateTaskStatus,
        getPlanById,
        searchPlans,

        // Actions de rafraîchissement
        refresh: fetchPlans,
        refreshPlans,
        refetchPlans,

        // Utilitaires
        clearError,
        
        // Cache management
        invalidateCache: () => invalidateCache('plans_action')
    };
};